/* ══════════════════════════════════════════════════════════════
   src/content/communities.tsx
   All static content for the Communities page with template variables
   for game mechanics and concepts.
   ══════════════════════════════════════════════════════════════ */

import { ReactNode } from 'react';
import {
  HOPE,
  FEAR,
  ADVANTAGE,
  FAR,
  HIT_POINT,
  STRESS,
} from './templates';

export interface CommunityFeature {
  name: string;
  text: ReactNode;
}

export interface Community {
  name: string;
  source?: 'Void' | 'HF';
  description?: string[];
  cardDescription?: string;
  features?: CommunityFeature[];
}

export const communities: Community[] = [
  {
    name: 'Highborne',
    description: [
      "Being part of a highborne community means you're accustomed to a life of elegance, opulence, and prestige within the upper echelons of society. Traditionally, members of a highborne community possess incredible material wealth. While this can take a variety of forms depending on the community—including gold and other minerals, land, or controlling the means of production—this status always comes with power and influence.",
      'Highborne place great value on titles and possessions, and there is little social mobility within their ranks. Members of a highborne community often control the political and economic status of the areas in which they live due to their ability to influence people and the economy with their substantial wealth. The health and safety of the less affluent people who live in these locations often hinges on the ability of this highborne ruling class to prioritize the well-being of their subjects over profit.',
      'Highborne are often amiable, candid, conniving, enterprising, ostentatious, and unflappable.',
    ],
    cardDescription: 'Being part of a highborne community means you’re accustomed to a life of elegance, opulence, and prestige within the upper echelons of society.',
    features: [
      { name: 'Privilege', text: <>You have {ADVANTAGE} on rolls to consort with nobles, negotiate prices, or leverage your reputation to get what you want.</> },
    ],
  },
  {
    name: 'Loreborne',
    description: [
      "Being part of a loreborne community means you're from a society that favors strong academic or political prowess. Loreborne communities highly value knowledge, frequently in the form of historical preservation, political advancement, scientific study, skill development, or lore and mythology compilation. Most members of these communities research in institutions built in bastions of civilization, while some eclectic few thrive in gathering information from the natural world.",
      'Some may be isolationists, operating in smaller enclaves, schools, or guilds and following their own unique ethos. Others still wield their knowledge on a larger scale, making deft political maneuvers across governmental landscapes.',
      'Loreborne are often direct, eloquent, inquisitive, patient, rhapsodic, and witty.',
    ],
    cardDescription: 'Being part of a loreborne community means you’re from a society that favors strong academic or political prowess.',
    features: [
      { name: 'Well-Read', text: <>You have {ADVANTAGE} on rolls that involve the history, culture, or politics of a prominent person or place.</> },
    ],
  },
  {
    name: 'Orderborne',
    description: [
      "Being part of an orderborne community means you're from a collective that focuses on discipline or faith, and you uphold a set of principles that reflect your experience there.",
      "Orderborne are frequently some of the most powerful among the surrounding communities. By aligning the members of their society around a common value or goal, such as a god, doctrine, ethos, or even a shared business or trade, the ruling bodies of these enclaves can mobilize larger populations with less effort. While orderborne communities take a variety of forms—some even profoundly pacifistic—perhaps the most feared are those that structure themselves around military prowess. In such a case, it's not uncommon for orderborne to provide soldiers for hire to other cities or countries.",
      'Orderborne are often ambitious, benevolent, pensive, prudent, sardonic, and stoic.',
    ],
    cardDescription: 'Being part of an orderborne community means you’re from a collective that focuses on discipline or faith, and you uphold a set of principles that reflect your experience there.',
    features: [
      { name: 'Dedicated', text: <>Record three sayings or values your upbringing instilled in you. Once per rest, when you describe how you're embodying one of these principles through your current action, you can roll a d20 as your {HOPE} Die.</> },
    ],
  },
  {
    name: 'Ridgeborne',
    description: [
      "Being part of a ridgeborne community means you've called the rocky peaks and sharp cliffs of the mountainside home. Those who've lived in the mountains often consider themselves hardier than most because they've thrived among the most dangerous terrain many continents have to offer. These groups are adept at adaptation, developing unique technologies and equipment to move both people and products across difficult terrain. As such, ridgeborne grow up scrambling and climbing, making them sturdy and strong-willed.",
      "Ridgeborne localities appear in a variety of forms—some cities carve out entire cliff faces, others construct castles of stone, and still more live in small homes on windblown peaks. Outside forces often struggle to attack ridgeborne groups, as the small militias and large military forces of the mountains are adept at utilizing their high-ground advantage.",
      'Ridgeborne are often bold, hardy, indomitable, loyal, reserved, and stubborn.',
    ],
    cardDescription: 'Being part of a ridgeborne community means you’ve called the rocky peaks and sharp cliffs of the mountainside home.',
    features: [
      { name: 'Steady', text: <>You have {ADVANTAGE} on rolls to traverse dangerous cliffs and ledges, navigate harsh environments, and use your survival knowledge.</> },
    ],
  },
  {
    name: 'Seaborne',
    description: [
      "Being part of a seaborne community means you lived on or near a large body of water. Seaborne communities are built, both physically and culturally, around the specific waters they call home. Some of these groups live along the shore, constructing ports for locals and travelers alike. These harbors function as centers of commerce, tourist attractions, or even just a safe place to lay down one's head after weeks of travel.",
      "Other seaborne live on the water in small boats or large ships, with the idea of \"home\" comprising a ship and its crew, rather than any one landmass. No matter their exact location, seaborne communities are closely tied to the ocean tides and the creatures who inhabit them. Seaborne learn to fish at a young age, and train from birth to hold their breath and swim in even the most tumultuous waters. Individuals from these groups are highly sought after for their sailing skills, and many become captains of vessels, whether within their own community, working for another, or even at the helm of a powerful naval operation.",
      'Seaborne are often candid, cooperative, exuberant, fierce, resolute, and weathered.',
    ],
    cardDescription: 'Being part of a seaborne community means you lived on or near a large body of water.',
    features: [
      { name: 'Know the Tide', text: <>You can sense the ebb and flow of life. When you roll with {FEAR}, place a token on your community card. You can hold a number of tokens equal to your level. Before you make an action roll, you can spend any number of these tokens to gain a +1 bonus to the roll for each token spent. At the end of each session, clear all unspent tokens.</> },
    ],
  },
  {
    name: 'Slyborne',
    description: [
      'Being part of a slyborne community means you come from a group that operates outside the law, including all manner of criminals, grifters, and con artists. Members of slyborne communities are brought together by their disreputable goals and their clever means of achieving them. Many people in these communities have an array of unscrupulous skills: forging, thievery, smuggling, and violence.',
      'People of any social class can be slyborne, from those who have garnered vast wealth and influence to those without a coin to their name. To the outside eye, slyborne might appear to be ruffians with no loyalty, but these communities possess some of the strictest codes of honor which, when broken, can result in a terrifying end for the transgressor.',
      'Slyborne are often calculating, clever, formidable, perceptive, shrewd, and tenacious.',
    ],
    cardDescription: 'Being part of a slyborne community means you come from a group that operates outside the law, including all manner of criminals, grifters, and con artists.',
    features: [
      { name: 'Scoundrel', text: <>You have {ADVANTAGE} on rolls to negotiate with criminals, detect lies, or find a safe place to hide.</> },
    ],
  },
  {
    name: 'Underborne',
    description: [
      "Being part of an underborne community means you're from a subterranean society. Many underborne live right beneath the cities and villages of other collectives, while some live much deeper. These communities range from small family groups in burrows to massive metropolises in caverns of stone. In many locales, underborne are recognized for their incredible boldness and skill that enable great feats of architecture and engineering.",
      "Underborne are regularly hired for their bravery, as even the least daring among them has likely encountered formidable belowground beasts, and learning to dispatch such creatures is common practice amongst these societies. Because of the dangers of their environment, many underborne communities develop unique nonverbal languages that prove equally useful on the surface.",
      'Underborne are often composed, elusive, indomitable, innovative, resourceful, and unpretentious.',
    ],
    cardDescription: 'Being part of an underborne community means you’re from a subterranean society.',
    features: [
      { name: 'Low-Light Living', text: <>When you're in an area with low light or heavy shadow, you have {ADVANTAGE} on rolls to hide, investigate, or perceive details within that area.</> },
    ],
  },
  {
    name: 'Wanderborne',
    description: [
      "Being part of a wanderborne community means you've lived as a nomad, forgoing a permanent home and experiencing a wide variety of cultures. Unlike many communities that are defined by their locale, wanderborne are defined by their traveling lifestyle. Because of their frequent migration, wanderborne put less value on the accumulation of material possessions in favor of acquiring information, skills, and connections.",
      'While some wanderborne are allied by a common ethos, such as a religion or a set of political or economic values, others come together after shared tragedy, such as the loss of their home or land. No matter the reason, the dangers posed by life on the road and the choice to continue down that road together mean that wanderborne are known for their unwavering loyalty.',
      'Wanderborne are often inscrutable, magnanimous, mirthful, reliable, savvy, and unorthodox.',
    ],
    cardDescription: 'Being part of a wanderborne community means you’ve lived as a nomad, forgoing a permanent home and experiencing a wide variety of cultures.',
    features: [
      { name: 'Nomadic Pack', text: <>Add a Nomadic Pack to your inventory. Once per session, you can spend a {HOPE} to reach into this pack and pull out a mundane item that's useful to your situation. Work with the GM to figure out what item you take out.</> },
    ],
  },
  {
    name: 'Wildborne',
    description: [
      "Being part of a wildborne community means you lived deep within the forest. Wildborne communities are defined by their dedication to the conservation of their homelands, and many have strong religious or cultural ties to the fauna they live among. This results in unique architectural and technological advancements that favor sustainability over short-term, high-yield results.",
      "It is a hallmark of wildborne societies to integrate their villages and cities with the natural environment and avoid disturbing the lives of the plants and animals. While some construct their lodgings high in the branches of trees, others establish their homes on the ground beneath the forest canopy. It's not uncommon for wildborne to remain reclusive and hidden within their woodland homes.",
      'Wildborne are often hardy, loyal, nurturing, reclusive, sagacious, and vibrant.',
    ],
    cardDescription: 'Being part of a wildborne community means you lived deep within the forest.',
    features: [
      { name: 'Lightfoot', text: <>Your movement is naturally silent. You have {ADVANTAGE} on rolls to move without being heard.</> },
    ],
  },

  /* ── Void Communities ── */
  {
    name: 'Duneborne',
    source: 'Void',
    description: [
      "Being part of a duneborne community means you've made a home among the shifting sands and arid climate of the desert.",
    ],
    cardDescription:
      'Being part of a duneborne community means you\'ve made a home among the shifting sands and arid climate of the desert.',
    features: [
      { name: 'Oasis', text: <>During a short rest, you or an ally can reroll a die used for a downtime action.</> },
    ],
  },
  {
    name: 'Freeborne',
    source: 'Void',
    description: [
      "Being part of a freeborne community means you're from a collective that lived under a tyrannical rule and is now liberated.",
    ],
    cardDescription:
      'Being part of a freeborne community means you\'re from a collective that lived under a tyrannical rule and is now liberated.',
    features: [
      { name: 'Unbound', text: <>Once per session, when you make an action roll with {FEAR}, you can change it to a roll with {HOPE} instead.</> },
    ],
  },
  {
    name: 'Frostborne',
    source: 'Void',
    description: [
      'Being part of a frostborne community means you come from a place of snow and ice.',
    ],
    cardDescription:
      'Being part of a frostborne community means you come from a place of snow and ice.',
    features: [
      { name: 'Hardy', text: <>Once per rest, you can Help an Ally traverse difficult terrain without spending a {HOPE}.</> },
    ],
  },
  {
    name: 'Hearthborne',
    source: 'Void',
    description: [
      'Coming from a hearthborne community means you come from humble origins, having lived in a small village or the countryside.',
    ],
    cardDescription:
      'Coming from a hearthborne community means you come from humble origins, having lived in a small village or the countryside.',
    features: [
      { name: 'Close-Knit', text: <>Once per long rest, you can spend any number of {HOPE} to give an ally the same number of {HOPE}.</> },
    ],
  },
  {
    name: 'Reborne',
    source: 'Void',
    description: [
      'You were once a member of another community, but you can no longer remember it.',
    ],
    cardDescription:
      'You were once a member of another community, but you can no longer remember it.',
    features: [
      { name: 'Found Family', text: <>Once per session, you can spend a {HOPE} to use an ally's community ability. When you do, your ally gains a {HOPE}.<br></br>At any point, when you've discovered the community you were once a part of, or have joined a new community, you can permanently trade this community card for that one instead.</> },
    ],
  },
  {
    name: 'Warborne',
    source: 'Void',
    description: [
      'Being part of a warborne community means you come from a place that is, or was, ravaged by war.',
    ],
    cardDescription:
      'Being part of a warborne community means you come from a place that is, or was, ravaged by war.',
    features: [
      { name: 'Brave Face', text: <>Once per session, when an attack would cause you to mark a Stress, you can spend a {HOPE} instead.</> },
    ],
  },

  /* ── Hope & Fear Communities (final versions of the Void communities above) ── */
  {
    name: 'Duneborne',
    source: 'HF',
    description: [
      "Being part of a duneborne community means you've made a home among the shifting sands and arid climate of the desert.",
    ],
    cardDescription:
      "Being part of a duneborne community means you've made a home among the shifting sands and arid climate of the desert.",
    features: [
      { name: 'Oasis', text: <>During a short rest, you or an ally can reroll a die used for a downtime move and take the higher result.</> },
    ],
  },
  {
    name: 'Freeborne',
    source: 'HF',
    description: [
      "Being part of a freeborne community means you're from a collective that once lived under tyrannical rule but is now liberated.",
    ],
    cardDescription:
      "Being part of a freeborne community means you're from a collective that once lived under tyrannical rule but is now liberated.",
    features: [
      { name: 'Unbound', text: <>Once per session when you roll with {FEAR}, you can change it into a roll with {HOPE} instead.</> },
    ],
  },
  {
    name: 'Frostborne',
    source: 'HF',
    description: [
      'Being part of a frostborne community means you come from a place of snow and ice.',
    ],
    cardDescription:
      'Being part of a frostborne community means you come from a place of snow and ice.',
    features: [
      { name: 'Hardy', text: <>When you take a rest, you clear a {HIT_POINT}.</> },
    ],
  },
  {
    name: 'Hearthborne',
    source: 'HF',
    description: [
      'Being part of a hearthborne community means you come from humble origins, having lived in a modest village or quaint countryside.',
    ],
    cardDescription:
      'Being part of a hearthborne community means you come from humble origins, having lived in a modest village or quaint countryside.',
    features: [
      { name: 'Close-Knit', text: <>Once per long rest, you can spend any number of {HOPE} to grant an ally within {FAR} range an equal number of {HOPE}.</> },
    ],
  },
  {
    name: 'Reborne',
    source: 'HF',
    description: [
      "You were once a member of a different community that you're no longer part of.",
    ],
    cardDescription:
      "You were once a member of a different community that you're no longer part of.",
    features: [
      { name: 'Found Family', text: <>Once per rest, you can spend a {HOPE} to use an ally's Experience as if it were your own. When you do so, describe how your time with that ally prepared you for this moment. When you join a new community or rediscover your old one, you can permanently trade this card for that one instead.</> },
    ],
  },
  {
    name: 'Warborne',
    source: 'HF',
    description: [
      'Being part of a warborne community means you come from a place that is, or was, ravaged by war.',
    ],
    cardDescription:
      'Being part of a warborne community means you come from a place that is, or was, ravaged by war.',
    features: [
      { name: 'Brave Face', text: <>Once per session when you would be forced to mark a {STRESS}, you can spend a {HOPE} instead.</> },
    ],
  },
];
